import { baseApi, PaginatedResponse } from 'sama-shared';
import type { PartnerCollectPayout, PartnerCollectPayoutDetails } from '../types/admin';
import type {
  ConfirmPartnerCollectPayoutDto,
  GetPartnerCollectPayoutsParams,
  UpdatePartnerCollectPayoutStatusDto,
} from '../types/api';

export const partnerCollectPayoutApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['PartnerCollectPayout', 'PartnerCollectSettlement'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPartnerCollectPayouts: builder.query<
        PaginatedResponse<PartnerCollectPayout>,
        GetPartnerCollectPayoutsParams
      >({
        query: (params) => ({
          url: '/admin/partner-payout',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'PartnerCollectPayout' as const, id })),
                { type: 'PartnerCollectPayout', id: 'LIST' },
              ]
            : [{ type: 'PartnerCollectPayout', id: 'LIST' }],
      }),

      getPartnerCollectPayoutById: builder.query<PartnerCollectPayoutDetails, string>({
        query: (id) => ({
          url: `/admin/partner-payout/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'PartnerCollectPayout', id }],
      }),

      confirmPartnerCollectPayout: builder.mutation<
        PartnerCollectPayout,
        { id: string; body: ConfirmPartnerCollectPayoutDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/partner-payout/${id}/confirm`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'PartnerCollectPayout', id },
          { type: 'PartnerCollectPayout', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),

      failPartnerCollectPayout: builder.mutation<
        PartnerCollectPayout,
        { id: string; body: ConfirmPartnerCollectPayoutDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/partner-payout/${id}/fail`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'PartnerCollectPayout', id },
          { type: 'PartnerCollectPayout', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),

      cancelPartnerCollectPayout: builder.mutation<PartnerCollectPayout, string>({
        query: (id) => ({
          url: `/admin/partner-payout/${id}/cancel`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'PartnerCollectPayout', id },
          { type: 'PartnerCollectPayout', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),

      /**
       * Reintenta un payout FAILED en el mismo id: mismo payoutNumber, mismos
       * settlements (siguen enlazados desde que failó, no se liberan solos),
       * vuelve a PROCESSING con failureReason limpio. El partner puede reportar
       * de nuevo sobre este mismo payoutId.
       * Ref: PARTNER_PAYOUT_PAYMENT_REPORT.md §Reprocesar un payout FAILED.
       */
      retryPartnerCollectPayout: builder.mutation<PartnerCollectPayout, string>({
        query: (id) => ({
          url: `/admin/partner-payout/${id}/retry`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'PartnerCollectPayout', id },
          { type: 'PartnerCollectPayout', id: 'LIST' },
        ],
      }),

      updatePartnerCollectPayoutStatus: builder.mutation<
        PartnerCollectPayout,
        { id: string; body: UpdatePartnerCollectPayoutStatusDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/partner-payout/${id}/status`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'PartnerCollectPayout', id },
          { type: 'PartnerCollectPayout', id: 'LIST' },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetPartnerCollectPayoutsQuery,
  useGetPartnerCollectPayoutByIdQuery,
  useConfirmPartnerCollectPayoutMutation,
  useFailPartnerCollectPayoutMutation,
  useCancelPartnerCollectPayoutMutation,
  useRetryPartnerCollectPayoutMutation,
  useUpdatePartnerCollectPayoutStatusMutation,
} = partnerCollectPayoutApi;
